import { acceptHMRUpdate, defineStore } from 'pinia'
import type { GetVendorsType, VendorType } from '@/types/vendor.type.js'
import CustomError from '@/js/custom-error.js'
import { Bugsnag } from '@/main.js'
import { Event } from '@bugsnag/js'
import { defaultCurrentVendor } from '@/views/orders/constants'

interface State {
  vendors: VendorType[]
  currentVendor: VendorType
  loadingVendors: boolean
}

export const useVendorsStore = defineStore('vendors', {
  state: (): State => {
    return {
      vendors: [],
      currentVendor: defaultCurrentVendor,
      loadingVendors: true
    }
  },
  getters: {
    owedVendors(state): VendorType[] {
      return state.vendors.filter((v) => v.invoices?.some((invoice) => invoice.balance > 0))
    }
  },
  actions: {
    async getVendors(params = {}) {
      this.loadingVendors = true
      try {
        const vendors: GetVendorsType = await $.get('/vendors', params)
        this.vendors = vendors
      } catch (error) {
        console.error('Error fetching vendors:', error)
        this.vendors = []
      } finally {
        this.loadingVendors = false
      }
    },
    async getVendorsInvoiceHistory(retailerId: string, context: string) {
      try {
        // refresh the list of vendors
        await this.getVendors()

        // load all of the invoice history for the retailer
        const addInvoicesPromises: any[] = []
        this.vendors.forEach((vendor) => {
          addInvoicesPromises.push(
            $.post('/vip/order-request-service/retailer/add-invoices', {
              account_code: vendor.vip_account_code,
              source_code: vendor.vip_distributor_source_code
            })
          )
        })
        const results = await Promise.allSettled(addInvoicesPromises)
        const failedInvoices: PromiseRejectedResult[] = []
        results.forEach((result) => {
          if (result.status === 'rejected') {
            failedInvoices.push(result)
          }
        })

        if (failedInvoices.length) {
          Bugsnag.notify(
            new CustomError(
              'POST /vip/order-request-service/retailer/add-invoices',
              new Error(
                `${failedInvoices.length} / ${addInvoicesPromises.length} failed to process`
              )
            ),
            function (event: Event) {
              event.severity = 'info'
              event.context = context
              event.addMetadata('request', {
                retailerId,
                body: failedInvoices.map((failedInvoice) => {
                  return failedInvoice.reason
                }),
                status: failedInvoices.map((failedInvoice) => failedInvoice.status),
                method: 'POST'
              })
            }
          )
        }
      } catch (error) {
        console.log('Failed to retrieve vendors', error)
      }
    },
    async getCurrentVendor(id: number) {
      const vendorData: VendorType = await $.get(`/vendors?id=${id}`)
      this.currentVendor = vendorData
      this.currentVendor.contacts.forEach((contact) => {
        contact.checked = false
      })
    },

    clearCurrentVendor() {
      this.currentVendor = defaultCurrentVendor
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVendorsStore, import.meta.hot))
}
